// TGCLOUD-1669 Phase 1 — collapsed-section summary lines.
//
// A collapsed accordion row has one job: answer "do I need to open this?".
// Today a row says DISEASES and nothing else, which is the failure the ticket
// exists for. These summarisers turn each row into
// `6 · Breast cancer dx 46, DCIS dx 46, +4 more`.
//
// Pure on purpose. No hooks, no fetching, no MUI — `resolveSidebarHeader` in
// ./util is the precedent, and it is pure for exactly this reason: the three
// fallbacks are testable without mounting a drawer. Everything time-varying
// (the date formatter) is injected.
//
// ---------------------------------------------------------------------------
// THE THREE-VALUED RETURN, and why it is three and not two
// ---------------------------------------------------------------------------
//   string    → render it
//   null      → render "none recorded"
//   undefined → render NOTHING; the answer has not arrived yet
//
// The third value is load-bearing. Phase 0 established that every section
// component is mounted whatever its accordion state (SidebarSection renders
// children unconditionally and MUI's Collapse does not unmount), so every
// fetch has already fired. But five sections keep the answer in their own
// useState and report it upward asynchronously. Between mount and that report
// there is a window in which we do not know whether the section is empty.
// Rendering "none recorded" in that window asserts a clinical absence that may
// be untrue — a patient with twelve risk scores would read as having none.
// `undefined` says "no claim yet" and renders quietly.
//
// CLINICAL INSIGHTS needs this most: its data arrives in two request waves.
//
// ---------------------------------------------------------------------------
// WHERE THE DATA COMES FROM
// ---------------------------------------------------------------------------
// Nine sections read straight off the member record. Bind those to the
// `memberRecords` row, NOT to the sidebar's `data` prop: `data` is a
// click-time snapshot (ChartBox builds it once per node click) and its only
// resync compares a seven-field structural allowlist — childrenIds, spouseIds,
// siblingIds, fatherId, motherId, spouseRelationships, dob — which excludes
// diagnoses, phenotypes and genotypes. A disease added in the panel would
// never reach `data`, so a `data`-bound count would show a stale number until
// the node was re-clicked. PedigreeSideBar resolves the member for us.
//
// The other five report through `facts`. See SECTION_FACT_SHAPES below.

import { SIDEBAR_VALUE } from './sidebarValues';
import {
  formatPartialDate,
  partialDateFromBackend
} from 'src/util/partialDate';
import {
  calculateAgePartial,
  formatAgeDisplay,
  getGenotypeDisplayParts
} from 'src/util/util';
import { patternLabel, ROLE_LABEL } from '../pedigree-tool/analysis/constants';
import {
  BUCKET,
  classificationBucket,
  leadingClassification,
  strictAcmgTier
} from 'src/util/variantPathogenicity';
import { english } from 'src/i18n/language';
import { isUnrecognisedCarrierStatus } from 'src/components/Constants';

// Middle dot, not a dash. Matches the agreed mock-ups.
export const SUMMARY_SEPARATOR = ' · ';

// Every summary takes useAutoTranslation's `t` (TGCLOUD-1893) for its own
// words; the record's values (terms, genes, tags, dates, identifiers) are
// filled into them and never sent to it. English, with the values filled in,
// when no `t` is given, which is what t() renders in English.
const ENGLISH = english;

/** Join the truthy parts with the separator; empty result becomes null. */
const join = (parts) => {
  const kept = parts.filter((p) => p !== null && p !== undefined && p !== '');
  return kept.length ? kept.join(SUMMARY_SEPARATOR) : null;
};

/**
 * The clinically leading items, then a count of what is left.
 * Two leads, because three truncates on a 400px row before the count is read.
 */
export const leadList = (items, max = 2, t = ENGLISH) => {
  const kept = (items || []).filter(Boolean);
  if (!kept.length) return '';
  const shown = kept.slice(0, max);
  const rest = kept.length - shown.length;
  return rest > 0
    ? t('{{items}}, +{{count}} more', { items: shown.join(', '), count: rest })
    : shown.join(', ');
};

const titleCase = (s) =>
  typeof s === 'string' && s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

/** `alias || name` — the label the canvas and the value sorter already use. */
const termLabel = (item) =>
  String(item?.alias || item?.name || '').trim() || null;

// ---------------------------------------------------------------------------
// PERSON
// ---------------------------------------------------------------------------
// Deliberately redundant with the header: it is a scroll target, not new
// information. Anonymous deployments drop the dates — `dob` and `dod` are both
// on PII_LABEL_KEYS — but keep sex, age and ethnicity, which are not
// identifying and which the risk models need.
export const personSummary = (
  member,
  { anonymous = false, t = ENGLISH } = {}
) => {
  if (!member) return undefined;

  const dobPartial = partialDateFromBackend({
    iso: member.dob,
    estimatedFlag: member.estimatedDob,
    precisionField: member.dobPrecision
  });
  const dodPartial = partialDateFromBackend({
    iso: member.dod,
    estimatedFlag: member.estimatedDod,
    precisionField: member.dodPrecision
  });

  // Deceased is the stored flag, not "a dod exists": a record can carry a date
  // of death with isAlive unset, and vice versa.
  const deceased = member.isAlive === 'false' || member.isAlive === false;

  // Never fabricate a day. formatPartialDate renders a year-precision value as
  // "1946", never "01/01/1946", which is how a partial date becomes a wrong
  // date. calculateAgePartial degrades to the worse of the two precisions and
  // formatAgeDisplay marks the result approximate.
  const ageResult = dobPartial
    ? calculateAgePartial(dobPartial, deceased ? dodPartial : null)
    : null;
  const ageText = ageResult
    ? formatAgeDisplay(ageResult)
    : member.age != null && member.age !== ''
    ? String(member.age)
    : null;

  const sex = titleCase(member.gender);
  const parts = [
    sex ? t(sex) : null,
    ageText
      ? deceased
        ? t('{{age}}y at death', { age: ageText })
        : t('{{age}}y', { age: ageText })
      : null
  ];

  if (!anonymous) {
    if (dobPartial)
      parts.push(t('b. {{date}}', { date: formatPartialDate(dobPartial) }));
    if (deceased && dodPartial)
      parts.push(t('d. {{date}}', { date: formatPartialDate(dodPartial) }));
  }

  const ethnicity = Array.isArray(member.ethnicity)
    ? member.ethnicity.filter(Boolean)
    : [];
  if (ethnicity.length) parts.push(ethnicity.join(', '));

  if (member.proBandId) parts.push(t('Proband'));

  return join(parts);
};

// ---------------------------------------------------------------------------
// DISEASES
// ---------------------------------------------------------------------------
export const diseasesSummary = (member, { t = ENGLISH } = {}) => {
  if (!member) return undefined;
  const items = Array.isArray(member.diagnoses) ? member.diagnoses : [];
  if (!items.length) return null;

  const labels = items
    .map((d) => {
      const term = termLabel(d);
      if (!term) return null;
      // Age at diagnosis is what turns a list into a family history.
      return d.age != null && d.age !== ''
        ? t('{{term}} dx {{age}}', { term, age: d.age })
        : term;
    })
    .filter(Boolean);

  return join([String(items.length), leadList(labels, 2, t)]);
};

// ---------------------------------------------------------------------------
// PHENOTYPES
// ---------------------------------------------------------------------------
// `observed: false` means the term was explicitly recorded as ABSENT, which is
// a positive clinical statement rather than a missing one. Counting those in
// with the observed terms would misreport the phenotype profile, so they are
// called out separately.
export const phenotypesSummary = (member, { t = ENGLISH } = {}) => {
  if (!member) return undefined;
  const items = Array.isArray(member.phenotypes) ? member.phenotypes : [];
  if (!items.length) return null;

  const observed = items.filter((p) => p.observed !== false);
  const absent = items.length - observed.length;

  return join([
    String(items.length),
    leadList(observed.map(termLabel), 2, t),
    absent > 0 ? t('{{count}} recorded absent', { count: absent }) : null
  ]);
};

// ---------------------------------------------------------------------------
// GENOTYPES
// ---------------------------------------------------------------------------
// Ordering here is clinical, never date or entry order: a pathogenic result is
// the single line most likely to change what the clinician does next, so it
// leads its section whatever order it was entered in.
const genotypeBucketRank = (genotype) => {
  const variants = Array.isArray(genotype?.variants) ? genotype.variants : [];
  let best = 3;
  variants.forEach((v) => {
    const lead = leadingClassification(v);
    const bucket = lead?.bucket || classificationBucket(v?.labInterpretation);
    if (bucket === BUCKET.pathogenic) best = Math.min(best, 0);
    else if (bucket === BUCKET.uncertain) best = Math.min(best, 1);
    else if (bucket === BUCKET.benign) best = Math.min(best, 2);
  });
  return best;
};

// A READABLE classification for prose. Deliberately not
// `classificationShortLabel`, which returns single-letter chip labels ("P",
// "B") — correct on a variant chip, unreadable in a sentence, and it made this
// line render "BRCA2 P". Pathogenic and likely pathogenic stay distinct
// because that distinction changes management; the rest collapse to a bucket
// word, since a 400px row cannot carry "Variant of Uncertain Significance".
const readableClassification = (raw) => {
  const tier = strictAcmgTier(raw);
  if (tier === 'Pathogenic') return 'pathogenic';
  if (tier === 'Likely Pathogenic') return 'likely pathogenic';
  if (tier === 'Variant of Uncertain Significance') return 'VUS';
  if (tier === 'Benign' || tier === 'Likely Benign') return 'benign';
  // Compound forms ("Pathogenic/Likely pathogenic", "Conflicting
  // classifications") get no strict tier by design, so fall back to severity.
  const bucket = classificationBucket(raw);
  if (bucket === BUCKET.pathogenic) return 'pathogenic';
  if (bucket === BUCKET.uncertain) return 'VUS';
  if (bucket === BUCKET.benign) return 'benign';
  return null;
};

const genotypeLabel = (genotype, t) => {
  const { symbol, description } = getGenotypeDisplayParts(genotype);
  const gene = symbol || description || genotype?.name || '';
  const variants = Array.isArray(genotype?.variants) ? genotype.variants : [];

  // The classification of the most serious variant, using the same leading
  // source the variant table shows, so the row cannot disagree with the panel.
  let leadLabel = null;
  let bestRank = 3;
  variants.forEach((v) => {
    const lead = leadingClassification(v);
    const raw = lead?.classification ?? v?.labInterpretation;
    const bucket = lead?.bucket || classificationBucket(raw);
    const rank =
      bucket === BUCKET.pathogenic
        ? 0
        : bucket === BUCKET.uncertain
        ? 1
        : bucket === BUCKET.benign
        ? 2
        : 3;
    if (rank < bestRank) {
      bestRank = rank;
      leadLabel = readableClassification(raw);
    }
  });

  if (leadLabel) return `${gene} ${t(leadLabel)}`.trim();
  // A status TrakGene knows reads as it is stored, in lower case like the
  // classification beside it ("MLH1 carrier"), translated. A value an
  // integration sent that TrakGene does not know is the record's own words,
  // shown as stored and never sent to t().
  if (genotype?.carrierStatus) {
    const status = isUnrecognisedCarrierStatus(genotype.carrierStatus)
      ? genotype.carrierStatus
      : t(genotype.carrierStatus);
    return `${gene} ${status}`.trim();
  }
  // A gene tested with nothing found is a RESULT, not an empty row, so say so
  // rather than rendering a bare gene symbol that reads as incomplete data.
  return gene ? t('{{gene}} no variant recorded', { gene }) : null;
};

export const genotypesSummary = (member, { t = ENGLISH } = {}) => {
  if (!member) return undefined;
  const items = Array.isArray(member.genotypes) ? member.genotypes : [];
  if (!items.length) return null;

  const ordered = items
    .map((g, i) => ({ g, i, rank: genotypeBucketRank(g) }))
    // Stable within a rank: keep entry order so the line does not reshuffle
    // between renders for two genotypes of equal severity.
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map(({ g }) => genotypeLabel(g, t));

  return join([String(items.length), leadList(ordered, 2, t)]);
};

// ---------------------------------------------------------------------------
// REPRODUCTIVE
// ---------------------------------------------------------------------------
export const reproductiveSummary = (member, { t = ENGLISH } = {}) => {
  if (!member) return undefined;
  const items = Array.isArray(member.pregnancies) ? member.pregnancies : [];
  if (!items.length) return null;
  return t('{{count}} recorded', { count: items.length });
};

// ---------------------------------------------------------------------------
// TAGS
// ---------------------------------------------------------------------------
export const tagsSummary = (member, { t = ENGLISH } = {}) => {
  if (!member) return undefined;
  const items = Array.isArray(member.personTags) ? member.personTags : [];
  const labels = items.map((t) => t?.tag).filter(Boolean);
  if (!labels.length) return null;
  return join([String(labels.length), leadList(labels, 3, t)]);
};

// ---------------------------------------------------------------------------
// EMR / external identifiers
// ---------------------------------------------------------------------------
// Identifiers are direct identifiers, so an anonymous deployment reports only
// that a link exists. The EMR section is already dropped from the item list in
// anonymous mode, so this is a second line of defence rather than the only one.
export const emrSummary = (member, { anonymous = false, t = ENGLISH } = {}) => {
  if (!member) return undefined;
  const ids = Array.isArray(member.externalPatientIds)
    ? member.externalPatientIds.filter(Boolean)
    : [];
  const single = member.externalPatientId ? [member.externalPatientId] : [];
  const all = ids.length ? ids : single;
  if (!all.length) return null;
  if (anonymous) return t('{{count}} linked', { count: all.length });
  return join([member.externalSource || t('Linked'), leadList(all, 2, t)]);
};

// ---------------------------------------------------------------------------
// RECORD STATUS
// ---------------------------------------------------------------------------
export const recordStatusSummary = (personLinkStatus, { t = ENGLISH } = {}) => {
  if (!personLinkStatus) return undefined;
  if (personLinkStatus === 'secondary')
    return t('Secondary record, editing locked');
  if (personLinkStatus === 'primary') return t('Primary record');
  return null;
};

// ---------------------------------------------------------------------------
// PEDIGREE ANALYSIS
// ---------------------------------------------------------------------------
// `analysis` is a client-side memo, but it is null until the VA feature flags
// settle, so absent means "not yet", not "nothing found" — undefined, not null.
//
// The panel renders TWO different views and the summary has to follow it. With a
// member selected it shows that individual's role, the family patterns they
// contribute to, and their cascade information. Only with no member selected
// does it show the family-wide pattern and aggregation counts.
//
// Summarising the family view while the panel showed the individual one is what
// produced `pattern.autosomal_recessive, +1 more · 3 aggregations` on a row
// whose panel listed one pattern and no aggregations: the count came from every
// disease in the FAMILY, and the id was a raw enum value because the label map
// lived in the panel rather than beside the enum.
export const pedigreeAnalysisSummary = (
  analysis,
  member,
  { t = ENGLISH } = {}
) => {
  if (!analysis) return undefined;

  // ---- individual view -----------------------------------------------------
  if (member) {
    const section = analysis.individuals?.[String(member.pid)];
    // The panel says "No analysis for this individual" here, so this is a
    // settled nothing rather than a not-yet.
    if (!section) return null;

    const patterns = (section.referencedPatterns || [])
      .filter(Boolean)
      .map((id) => t(patternLabel(id)));
    const cascades = Array.isArray(section.individualActions)
      ? section.individualActions.length
      : 0;

    return join([
      section.role
        ? ROLE_LABEL[section.role]
          ? t(ROLE_LABEL[section.role])
          : section.role
        : null,
      patterns.length ? leadList(patterns, 2, t) : null,
      // Counted, not quoted. The figure on each card is a carrier probability
      // with its derivation attached, and a bare percentage on a collapsed row
      // would be the one part of it a clinician could not check.
      cascades
        ? cascades === 1
          ? t('{{count}} cascade estimate', { count: cascades })
          : t('{{count}} cascade estimates', { count: cascades })
        : null
    ]);
  }

  // ---- family view ---------------------------------------------------------
  const family = analysis.family || {};
  const patterns = Array.isArray(family.inheritancePatterns)
    ? family.inheritancePatterns
    : [];
  // A DIFFERENT claim from an inheritance pattern, not a weaker one: these are
  // diseases where relatives share a diagnosis but no mode of inheritance may be
  // asserted. Named in full so the row cannot be read as a pattern count.
  const aggregations = Array.isArray(family.familialAggregations)
    ? family.familialAggregations
    : [];

  return join([
    patterns.length
      ? leadList(
          patterns
            .map((p) => patternLabel(p?.id))
            .filter(Boolean)
            .map((label) => t(label)),
          2,
          t
        )
      : null,
    aggregations.length
      ? aggregations.length === 1
        ? t('{{count}} familial aggregation', { count: aggregations.length })
        : t('{{count}} familial aggregations', { count: aggregations.length })
      : null
  ]);
};

// ---------------------------------------------------------------------------
// Sections that carry an action hint rather than a count
// ---------------------------------------------------------------------------
// ADD RELATIVE and EXPORT are verbs, not nouns. They have nothing to count, so
// pretending they are data sections to keep the pattern tidy would be worse
// than admitting they are not.
export const ADD_RELATIVE_HINT = 'Add parents, partner, child or sibling';
export const EXPORT_HINT = 'PDF, PNG, GEDCOM, BOADICEA';

// ---------------------------------------------------------------------------
// The five reported sections
// ---------------------------------------------------------------------------
/**
 * What each reporting section hands upward. Every shape carries `status`,
 * because "not yet known" and "known to be empty" must not collapse into one
 * value. `status: 'pending'` yields undefined; 'ready' with nothing yields null.
 *
 *  clinicalNotes  { status, count, byType: {type: n}, lastDate, nextDate,
 *                   didNotAttend }
 *  riskFactors    { status, count, leads: string[] }
 *  formSubmission { status, count, latest }
 *  pathways       { status, count, active, nextDue }
 *  suggestions    { status, phenotypeCount, diseaseCount }
 */
// Stable, order-insensitive serialisation, used to decide whether a section's
// re-report actually changed anything.
//
// A plain shallow compare is NOT enough: `clinicalNotes.byType` is a nested
// object, so shallow equality would compare it by identity, see a fresh object
// on every render and set state again, which sets up a render loop. Key order
// is normalised too, so a byType map rebuilt in a different insertion order
// does not read as a change.
const stableKey = (v) => {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null';
  if (Array.isArray(v)) return `[${v.map(stableKey).join(',')}]`;
  return `{${Object.keys(v)
    .sort()
    .map((k) => `${k}:${stableKey(v[k])}`)
    .join(',')}}`;
};

/** True when two reported fact payloads say the same thing. */
export const factsEqual = (a, b) => stableKey(a) === stableKey(b);

export const SECTION_FACT_SHAPES = Object.freeze({
  [SIDEBAR_VALUE.clinicalNotes]: 'clinical records',
  [SIDEBAR_VALUE.riskFactors]: 'risk scores',
  [SIDEBAR_VALUE.formSubmission]: 'form submissions',
  [SIDEBAR_VALUE.pathways]: 'pathways',
  [SIDEBAR_VALUE.suggestions]: 'clinical insights'
});

const pending = (facts) => !facts || facts.status === 'pending';

// The CLINICAL RECORDS count deliberately EXCLUDES diseases, phenotypes,
// genotypes and risk scores. The record browser's count includes them, so the
// same patient reads 37 here and 60 there. That is not records going missing:
// this section lists clinical notes, the browser lists every record type.
export const clinicalRecordsSummary = (facts, { t = ENGLISH } = {}) => {
  if (pending(facts)) return undefined;
  // No "N private withheld" part any more (TGCLOUD-1766): the Desktop private
  // flag is retired, so nothing is withheld and there is no count to report.
  if (!facts.count) return null;
  const byType = facts.byType || {};
  const ordered = Object.keys(byType)
    .filter((t) => byType[t] > 0)
    .sort((a, b) => byType[b] - byType[a] || a.localeCompare(b))
    .map((type) => `${t(type)} ${byType[type]}`);

  return join([
    String(facts.count),
    leadList(ordered, 4, t),
    // A history of missed appointments, said on the collapsed row because it
    // is the thing a clinician opening this person needs to know before
    // booking another (TGCLOUD-1751). Only when there is one: "0 did not
    // attend" would be noise on every row.
    facts.didNotAttend > 0
      ? t('{{count}} did not attend', { count: facts.didNotAttend })
      : null,
    // Last and next are separate on purpose. A single "latest" that resolved to
    // a booked appointment reads as a record from the future.
    facts.lastDate ? t('last {{date}}', { date: facts.lastDate }) : null,
    facts.nextDate ? t('next {{date}}', { date: facts.nextDate }) : null
  ]);
};

export const riskScoresSummary = (facts, { t = ENGLISH } = {}) => {
  if (pending(facts)) return undefined;
  if (!facts.count) return null;
  return join([String(facts.count), leadList(facts.leads || [], 2, t)]);
};

export const formSubmissionsSummary = (facts) => {
  if (pending(facts)) return undefined;
  if (!facts.count) return null;
  return join([String(facts.count), facts.latest || null]);
};

export const pathwaysSummary = (facts, { t = ENGLISH } = {}) => {
  if (pending(facts)) return undefined;
  if (!facts.count) return null;
  return join([
    facts.active
      ? t('{{count}} active', { count: facts.active })
      : t('{{count}} recorded', { count: facts.count }),
    facts.nextDue ? t('next due {{date}}', { date: facts.nextDue }) : null
  ]);
};

// Clinical insights is the section the three-valued return exists for. Its
// inputs arrive on one wave and the ranked matches on a second, so this
// summarises the INPUTS only. The mock-up shows a top-ranked disease instead;
// reaching that would mean lifting through ProfileMatchBox and waiting on the
// second wave, which is a bigger change than the summary is worth. Flagged for
// Kathryn at review.
export const clinicalInsightsSummary = (facts, { t = ENGLISH } = {}) => {
  if (pending(facts)) return undefined;
  const p = facts.phenotypeCount || 0;
  const d = facts.diseaseCount || 0;
  if (!p && !d) return null;
  return join([
    p
      ? p === 1
        ? t('{{count}} phenotype', { count: p })
        : t('{{count}} phenotypes', { count: p })
      : null,
    d
      ? d === 1
        ? t('{{count}} disease', { count: d })
        : t('{{count}} diseases', { count: d })
      : null
  ]);
};

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Counts, for the promoted view's section index
// ---------------------------------------------------------------------------
/**
 * How many items each section holds, keyed by SIDEBAR_VALUE.
 *
 * `undefined` where a count is not knowable yet or does not apply, which is the
 * same three-valued idea the summaries use: the index renders nothing rather
 * than a `0` that would read as a settled clinical absence while a section is
 * still reporting.
 */
export function buildSectionCounts(input = {}) {
  const { member = null, facts = {} } = input;
  if (!member) return {};

  const len = (v) => (Array.isArray(v) ? v.length : undefined);
  const reported = (key, pick) => {
    const f = facts[key];
    if (!f || f.status === 'pending') return undefined;
    return pick(f);
  };

  return {
    [SIDEBAR_VALUE.clinicalNotes]: reported(
      SIDEBAR_VALUE.clinicalNotes,
      (f) => f.count
    ),
    [SIDEBAR_VALUE.diagnoses]: len(member.diagnoses),
    [SIDEBAR_VALUE.phenotypes]: len(member.phenotypes),
    [SIDEBAR_VALUE.genotypes]: len(member.genotypes),
    [SIDEBAR_VALUE.reproductive]: len(member.pregnancies),
    [SIDEBAR_VALUE.riskFactors]: reported(
      SIDEBAR_VALUE.riskFactors,
      (f) => f.count
    ),
    [SIDEBAR_VALUE.formSubmission]: reported(
      SIDEBAR_VALUE.formSubmission,
      (f) => f.count
    ),
    [SIDEBAR_VALUE.pathways]: reported(SIDEBAR_VALUE.pathways, (f) => f.count),
    [SIDEBAR_VALUE.tags]: len(member.personTags),
    [SIDEBAR_VALUE.emr]: len(member.externalPatientIds)
  };
}

/**
 * Build every person-view summary in one pass.
 *
 * The family view is deliberately not summarised in Phase 1: its item list is
 * a different set of keys and none of them is the "DISEASES and nothing else"
 * failure the ticket is about. Those rows simply receive undefined.
 *
 * @param {object} input
 * @param {object|null} input.member  the memberRecords row for the selected node
 * @param {object|null} input.analysis
 * @param {string|null} input.personLinkStatus
 * @param {boolean} input.anonymous
 * @param {object} input.facts  keyed by SIDEBAR_VALUE, see SECTION_FACT_SHAPES
 * @param {Function} [input.t]  useAutoTranslation's t; English when omitted
 * @returns {Object<string, string|null|undefined>}
 */
export function buildSectionSummaries(input = {}) {
  const {
    member = null,
    analysis = null,
    personLinkStatus = null,
    anonymous = false,
    facts = {},
    t = ENGLISH
  } = input;
  const opts = { t };

  // Family view: no member, so nothing here applies.
  if (!member) return {};

  return {
    [SIDEBAR_VALUE.person]: personSummary(member, { anonymous, t }),
    [SIDEBAR_VALUE.clinicalNotes]: clinicalRecordsSummary(
      facts[SIDEBAR_VALUE.clinicalNotes],
      opts
    ),
    [SIDEBAR_VALUE.diagnoses]: diseasesSummary(member, opts),
    [SIDEBAR_VALUE.phenotypes]: phenotypesSummary(member, opts),
    [SIDEBAR_VALUE.genotypes]: genotypesSummary(member, opts),
    [SIDEBAR_VALUE.addRelative]: t(ADD_RELATIVE_HINT),
    [SIDEBAR_VALUE.reproductive]: reproductiveSummary(member, opts),
    [SIDEBAR_VALUE.riskFactors]: riskScoresSummary(
      facts[SIDEBAR_VALUE.riskFactors],
      opts
    ),
    [SIDEBAR_VALUE.suggestions]: clinicalInsightsSummary(
      facts[SIDEBAR_VALUE.suggestions],
      opts
    ),
    [SIDEBAR_VALUE.pedigreeMatch]: pedigreeAnalysisSummary(
      analysis,
      member,
      opts
    ),
    [SIDEBAR_VALUE.formSubmission]: formSubmissionsSummary(
      facts[SIDEBAR_VALUE.formSubmission]
    ),
    // FAMILY HISTORY is the one section whose fetch is genuinely gated on
    // expansion (it takes an `active` prop), so there is nothing to summarise
    // while collapsed and nothing is claimed.
    [SIDEBAR_VALUE.familyHistory]: undefined,
    [SIDEBAR_VALUE.tags]: tagsSummary(member, opts),
    [SIDEBAR_VALUE.emr]: emrSummary(member, { anonymous, t }),
    [SIDEBAR_VALUE.pathways]: pathwaysSummary(
      facts[SIDEBAR_VALUE.pathways],
      opts
    ),
    [SIDEBAR_VALUE.recordStatus]: recordStatusSummary(personLinkStatus, opts),
    [SIDEBAR_VALUE.export]: EXPORT_HINT
  };
}
